import { performanceFeedback } from "@/game-lol/components/TimelinePerformanceFeedback.jsx";
import {
  PRIMARY_CLASSES,
  SECONDARY_CLASSES,
} from "@/game-lol/constants/champion-classes.mjs";
import {
  COACHABLE_QUEUES,
  MADEUP_VISION_SCORE_PER_MIN,
  SCORE_MAX,
  SCORE_MIN,
  STAT_COMPARE,
} from "@/game-lol/constants/coaching-constants.mjs";
import {
  LANE_PHASE_END,
  QUEUE_SYMBOLS,
} from "@/game-lol/constants/constants.mjs";
import type { ChampionByMinuteStats } from "@/game-lol/models/lol-champion-by-minute-stats.mjs";
import type { Match, MatchParticipant } from "@/game-lol/models/lol-match.mjs";
import type { MatchTimeline } from "@/game-lol/models/lol-match-timeline.mjs";
import {
  getChampionPrimaryClass,
  getChampionSubClass,
} from "@/game-lol/utils/champion-utils.mjs";
import type { PlayerPerformance } from "@/game-lol/utils/coaching-types.mjs";
import { calculateAvgMinionValue } from "@/game-lol/utils/match-utils.mjs";
import QueueSymbol from "@/game-lol/utils/symbol-queue.mjs";
import { analyzeTimelinePerformance } from "@/game-lol/utils/timeline-performance-utils.mjs";
import Ward from "@/inline-assets/hextech-match-ward.svg";
import Mask from "@/inline-assets/hextech-minion-mask.svg";
import Swords from "@/inline-assets/hextech-stats-melee.svg";
import { statPerformance } from "@/util/coaching.mjs";
import mapRange from "@/util/map-range.mjs";

const ST_DEV_MULTI = 1.0;
const ST_DEV_MULTI_ARAM = 0.5;

export function isSRCoachableQueue(
  match: { queueId: string | number } = { queueId: 0 },
) {
  return COACHABLE_QUEUES[QueueSymbol(match.queueId)];
}

export function matchTooShort(queue, match) {
  const mins = (match?.gameDuration ?? 0) / 60;

  switch (queue) {
    case QUEUE_SYMBOLS.aram:
    case QUEUE_SYMBOLS.clashAram:
      return mins < 10;
    default:
    case undefined:
    case QUEUE_SYMBOLS.rankedSoloDuo:
      return mins < LANE_PHASE_END;
  }
}

export function isStatLow(playerVal, benchmarkVal) {
  return playerVal / benchmarkVal <= STAT_COMPARE.low;
}

export function scoreDisplay(val = 0, inMin, inMax) {
  return mapRange(val, inMin, inMax, SCORE_MIN, SCORE_MAX, true);
}

// function within(val, min, max) {
//   return val >= min && val <= max;
// }

export function createCategory({
  title,
  subtitle,
  icon,
  key,
  feedback = {
    supertitle: null,
    title: null,
    subtitle: null,
    content: null,
  },
}): PlayerPerformance["categories"][number] {
  return {
    key,
    title,
    subtitle,
    explanation: null,
    icon,
    performance: {
      min: 0,
      max: 0,
      score: 0,
      val: 0,
    },
    stats: [],
    impact: 0,
    feedback: {
      supertitle: feedback.supertitle,
      title: feedback.title,
      subtitle: feedback.subtitle,
      content: feedback.content,
    },
  };
}
export const QUEUE_CATEGORIES = {
  [QUEUE_SYMBOLS.rankedSoloDuo]: [
    createCategory({
      key: "combat",
      title: ["lol:coaching.combat", "Combat"],
      subtitle: [
        "lol:coaching.combatExplanation",
        "Your combat score represents your performance at battling enemies",
      ],
      icon: Swords,
      feedback: {
        supertitle: ["lol:coaching.deaths.title", "Deaths Performance"],
        title: null,
        subtitle: null,
        content: null,
      },
    }),
    createCategory({
      key: "farming",
      title: ["lol:coaching.farming", "Farming"],
      subtitle: [
        "lol:coaching.farmingExplanation",
        "Your farming score represents your performance at gaining power",
      ],
      icon: Mask,
      feedback: {
        supertitle: ["lol:coaching.farming.title", "Farming Performance"],
        title: null,
        subtitle: null,
        content: null,
      },
    }),
    createCategory({
      key: "vision",
      title: ["lol:coaching.vision", "Vision"],
      subtitle: [
        "lol:coaching.visionExplanation",
        "Your vision score represents your performance at gaining vision for your team and keeping your enemies in the dark",
      ],
      icon: Ward,
      feedback: {
        supertitle: ["lol:coaching.vision.title", "Vision Performance"],
        title: null,
        subtitle: null,
        content: null,
      },
    }),
  ],

  [QUEUE_SYMBOLS.aram]: [
    createCategory({
      title: ["lol:coaching.damage", "Damage"],
      subtitle: [
        "lol:coaching.damageExplanation",
        "Your damage score represents your performance dealing damage to enemies",
      ],
      icon: Swords,
      key: "damage",
    }),
    createCategory({
      title: ["lol:coaching.combat", "Combat"],
      subtitle: [
        "lol:coaching.combatExplanation",
        "Your combat score represents your performance at battling enemies",
      ],
      icon: Swords,
      key: "combat",
    }),
  ],

  [QUEUE_SYMBOLS.clashAram]: [
    createCategory({
      title: ["lol:coaching.damage", "Damage"],
      subtitle: [
        "lol:coaching.damageExplanation",
        "Your damage score represents your performance dealing damage to enemies",
      ],
      icon: Swords,
      key: "damage",
    }),

    createCategory({
      title: ["lol:coaching.combat", "Combat"],
      subtitle: [
        "lol:coaching.combatExplanation",
        "Your combat score represents your performance at battling enemies",
      ],
      icon: Swords,
      key: "combat",
    }),
  ],
};

function summonersRiftStats({
  localPlayer,
  match,
  timeline,
  benchmarkData,
  playerPerformance,
  lateGameStartTimestamp,
  playerRank,
}: {
  localPlayer: MatchParticipant;
  match: Match;
  timeline: MatchTimeline;
  benchmarkData: ChampionByMinuteStats;
  playerPerformance: PlayerPerformance;
  lateGameStartTimestamp: number;
  playerRank: symbol;
}) {
  const eog = match.gameDuration / 60 || 1; // end of game
  const eogFloor = Math.floor(eog);
  const endOfMidGame = lateGameStartTimestamp
    ? Math.floor(lateGameStartTimestamp / 60000)
    : eogFloor;

  // const playerRole = localPlayer.teamPosition;
  const playerTeamId = localPlayer.teamId;
  const championSubClass = getChampionSubClass(localPlayer.championId);

  const benchmarkMins = benchmarkData[Math.round(eog)]
    ? Math.round(eog)
    : benchmarkData.length
      ? benchmarkData.length
      : 1;
  const eogBenchmark = benchmarkData[benchmarkMins]
    ? benchmarkData[benchmarkMins]
    : benchmarkData.at(-1);

  if (!eogBenchmark) return;

  // 1) Combat
  const combat = playerPerformance.categories.find((c) => c.key === "combat");
  // Damage
  const playerDamage = localPlayer.totalDamageDealtToChampions / eog;
  const damageScore = statPerformance({
    playerVal: playerDamage,
    benchmarkMean:
      eogBenchmark.total_damage_done_to_champions?.mean / benchmarkMins,
    benchmarkStdev:
      eogBenchmark.total_damage_done_to_champions?.stdev / benchmarkMins,
  });
  combat.stats[0] = {
    key: "damagePerMinute",
    weight: 1,
    title: ["common:stats.damagePerMin", "Damage / Min."],
    score: damageScore,
    format: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
  };

  // Kill participation
  const teamKills = match.participants.reduce((acc, curr) => {
    if (curr.teamId !== playerTeamId) return acc;
    return acc + curr.kills;
  }, 0);
  const playerParticipation =
    (localPlayer.kills + localPlayer.assists) / (teamKills || 1);
  combat.stats[1] = {
    key: "killParticipation",
    weight: 1,
    title: ["lol:coaching.killParticipation", "Kill Participation"],
    score: statPerformance({
      playerVal: playerParticipation,
      benchmarkMean: eogBenchmark.kp?.mean,
      benchmarkStdev: eogBenchmark.kp?.stdev,
      lowerBoundStdevMultiplier: ST_DEV_MULTI,
      upperBoundStdevMultiplier: ST_DEV_MULTI,
    }),
    format: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      style: "percent",
    },
  };

  // KDA
  const benchmarkKills = eogBenchmark.kills;
  const benchmarkDeaths = eogBenchmark.deaths;
  const benchmarkAssists = eogBenchmark.assists;
  const benchmarkMeanKDA =
    (benchmarkKills?.mean + benchmarkAssists?.mean) /
    (benchmarkDeaths?.mean || 1);
  const benchmarkStdevKDA =
    (benchmarkKills?.stdev + benchmarkAssists?.stdev) /
    (benchmarkDeaths?.stdev || 1);
  const playerKDA =
    (localPlayer.kills + localPlayer.assists) / (localPlayer.deaths || 1);
  const kdaParams = {
    playerVal: playerKDA,
    benchmarkMean: benchmarkMeanKDA,
    benchmarkStdev: benchmarkStdevKDA,
    lowerBoundStdevMultiplier: 0.75,
    upperBoundStdevMultiplier: 0.75,
  };

  // Class-specific stats (e.g. warden, assassin, etc) see champion-classes.mjs
  // all these classes use KDA as a combat stats
  const tankingClasses = [
    SECONDARY_CLASSES.SKIRMISHER,
    SECONDARY_CLASSES.VANGUARD,
    SECONDARY_CLASSES.WARDEN,
  ];
  const kdaClasses = [
    SECONDARY_CLASSES.JUGGERNAUT,
    SECONDARY_CLASSES.DIVER,
    SECONDARY_CLASSES.DIVER,
    SECONDARY_CLASSES.BATTLEMAGE,
    SECONDARY_CLASSES.BURST,
    SECONDARY_CLASSES.ARTILLERY,
    PRIMARY_CLASSES.MARKSMAN,
    SECONDARY_CLASSES.ASSASSIN,
    SECONDARY_CLASSES.CATCHER,
  ];

  if (championSubClass === SECONDARY_CLASSES.ENCHANTER) {
    // Healing + shielding
    // cant really do because timeline doesnt have this
    // combat.stats[2] = {
    //   weight: 1,
    //   title: ["", "Healing + Shielding"],
    //   score: statPerformance({
    //     playerVal: 0,
    //     benchmarkMean: 0,
    //     benchmarkStdev: 0,
    //   }),
    //   format: {},
    // };
    combat.stats[2] = {
      key: "kda",
      weight: 1,
      title: ["common:stats.kda", "KDA"],
      score: statPerformance(kdaParams),
      format: {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      },
    };
  } else if (tankingClasses.includes(championSubClass)) {
    // Tanking
    const playerTanking =
      localPlayer.totalDamageTaken / (localPlayer.deaths || 1);
    const benchmarkTanking =
      eogBenchmark.total_damage_taken?.mean / (eogBenchmark.deaths?.mean || 1);
    const benchmarkTankingStdev =
      eogBenchmark.total_damage_taken?.mean / (eogBenchmark.deaths?.stdev || 1);

    combat.stats[2] = {
      key: "damageTakenPerDeath",
      weight: 1,
      title: ["lol:coaching.damageTakenPerDeath", "Dmg Taken / Death"],
      score: statPerformance({
        playerVal: playerTanking,
        benchmarkMean: benchmarkTanking,
        benchmarkStdev: benchmarkTankingStdev,
        lowerBoundStdevMultiplier: ST_DEV_MULTI,
        upperBoundStdevMultiplier: ST_DEV_MULTI,
      }),
      format: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
    };
  } else if (kdaClasses.includes(championSubClass)) {
    combat.stats[2] = {
      key: "kda",
      weight: 1,
      title: ["common:stats.kda", "KDA"],
      score: statPerformance(kdaParams),
      format: {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      },
    };
  } else {
    // fallback in case we didnt find a class for the champ
    combat.stats[2] = {
      key: "kda",
      weight: 1,
      title: ["common:stats.kda", "KDA"],
      score: statPerformance(kdaParams),
      format: {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      },
    };
  }

  // 2) Farming

  const farming = playerPerformance.categories.find((c) => c.key === "farming");
  const csFormat = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  const {
    participantId,
    totalMinionsKilled = 0,
    neutralMinionsKilled = 0,
  } = localPlayer;
  const frames = timeline?.frames || [];
  const endOfLane = frames[LANE_PHASE_END]?.participantFrames?.[participantId];
  const endOfLaneCS =
    (endOfLane?.minionsKilled || 0) + (endOfLane?.jungleMinionsKilled || 0);

  const endOfMid = frames[endOfMidGame]?.participantFrames?.[participantId];
  const endOfMidCS = endOfMid
    ? (endOfMid?.minionsKilled || 0) + (endOfMid?.jungleMinionsKilled || 0)
    : totalMinionsKilled + neutralMinionsKilled;
  const endOfGameCS = endOfMidGame
    ? totalMinionsKilled + neutralMinionsKilled
    : 0;

  function calculateCSDiff(playerEndCS, playerStartCS, minEnd, minStart) {
    const csGained = playerEndCS - playerStartCS;
    const endOfPhase = benchmarkData[minEnd];
    const startOfPhase = benchmarkData[minStart];

    const endCS = endOfPhase?.creep_score || { mean: 0, stdev: 0 };
    const endJungleCS = endOfPhase?.jungle_creep_score || { mean: 0, stdev: 0 };
    const startCS = startOfPhase?.creep_score || { mean: 0, stdev: 0 };
    const startJungleCS = startOfPhase?.jungle_creep_score || {
      mean: 0,
      stdev: 0,
    };

    const endMean = Math.round(endCS.mean + endJungleCS.mean);
    const startMean = Math.round(startCS.mean + startJungleCS.mean);
    const endStdev = Math.round(endCS.stdev + endJungleCS.stdev);
    const startStdev = Math.round(startCS.stdev + startJungleCS.stdev);
    const csDiff = csGained - (endMean - startMean);
    const goldValue = Math.round(
      csDiff * calculateAvgMinionValue(minEnd * 60000),
    );

    return {
      csDiff,
      goldValue,
      csGained,
      benchmarkMean: endMean - startMean,
      benchmarkStdev: endStdev - startStdev,
    };
  }

  // TODO: Make these non-support only??
  // CS lane phase
  if (endOfLaneCS) {
    const { csDiff, goldValue, csGained, benchmarkMean, benchmarkStdev } =
      calculateCSDiff(endOfLaneCS, 0, LANE_PHASE_END, 0);
    farming.stats[0] = {
      key: "csLane",
      weight: 1,
      title: ["lol:coaching.csDashLanePhase", "CS - Lane Phase"],
      score: statPerformance({
        playerVal: csGained,
        benchmarkMean,
        benchmarkStdev,
        round: true,
      }),
      format: csFormat,
      misc: {
        csDiff,
        goldValue,
        timestamp: LANE_PHASE_END * 60000,
      },
    };
  }
  if (endOfMidCS) {
    const { csDiff, goldValue, csGained, benchmarkMean, benchmarkStdev } =
      calculateCSDiff(endOfMidCS, endOfLaneCS, endOfMidGame, LANE_PHASE_END);
    farming.stats[1] = {
      key: "csMid",
      weight: 1,
      title: ["lol:coaching.csDashMidGame", "CS - Mid Game"],
      score: statPerformance({
        playerVal: csGained,
        benchmarkMean,
        benchmarkStdev,
        round: true,
      }),
      format: csFormat,
      misc: {
        csDiff,
        goldValue,
        timestamp: lateGameStartTimestamp || match.gameDuration * 1000,
      },
    };
  }
  // CS End of game
  if (endOfGameCS) {
    const { csDiff, goldValue, csGained, benchmarkMean, benchmarkStdev } =
      calculateCSDiff(endOfGameCS, endOfMidCS, eogFloor, endOfMidGame);
    farming.stats[2] = {
      key: "csLate",
      weight: 1,
      disabled: endOfMidGame === eogFloor,
      title: ["lol:coaching.csDashLateGame", "CS - Late Game"],
      score: statPerformance({
        playerVal: csGained,
        benchmarkMean,
        benchmarkStdev,
        round: true,
      }),
      format: csFormat,
      misc: {
        csDiff,
        goldValue,
        timestamp: eog * 60000,
      },
    };
  }

  // XP
  // if (localPlayer.champExperience) {
  //   const playerXP = localPlayer.champExperience / eog;
  //   farming.stats[2] = {
  //     weight: 1,
  //     title: ["lol:coaching.xpPerMin", "XP / Min."],
  //     score: statPerformance({
  //       playerVal: playerXP,
  //       benchmarkMean: benchmarkData.xpByMinute[eogFloor]?.mean / eog,
  //       benchmarkStdev: benchmarkData.xpByMinute[eogFloor]?.stdev / eog,
  //     }),
  //     format: {
  //       minimumFractionDigits: 0,
  //       maximumFractionDigits: 0,
  //     },
  //   };
  // }

  // 3) Vision
  const vision = playerPerformance.categories[2];
  if (vision) {
    // Vision score
    const playerVision = localPlayer.visionScore / eog;
    vision.stats[0] = {
      key: "visionScore",
      weight: 1,
      title: ["lol:coaching.visionScorePerMin", "Vision Score / Min."],
      score: statPerformance({
        playerVal: playerVision,
        benchmarkMean: MADEUP_VISION_SCORE_PER_MIN[playerRank],
        benchmarkStdev: MADEUP_VISION_SCORE_PER_MIN[playerRank] / 4,
        lowerBoundStdevMultiplier: ST_DEV_MULTI,
        upperBoundStdevMultiplier: ST_DEV_MULTI,
      }),
      format: {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      },
    };

    // Wards placed
    const playerWardsPlaced = localPlayer.wardsPlaced;
    vision.stats[1] = {
      key: "wardsPlaced",
      weight: 1,
      title: [
        "lol:postmatch.advancedStatsSubHeaders.wardsPlaced",
        "Wards Placed",
      ],
      score: statPerformance({
        playerVal: playerWardsPlaced,
        benchmarkMean:
          eogBenchmark.yellow_trinket_placed?.mean +
          eogBenchmark.control_ward_placed?.mean +
          eogBenchmark.sight_ward_placed?.mean,
        benchmarkStdev:
          eogBenchmark.yellow_trinket_placed?.stdev +
          eogBenchmark.control_ward_placed?.stdev +
          eogBenchmark.sight_ward_placed?.stdev,
        lowerBoundStdevMultiplier: ST_DEV_MULTI,
        upperBoundStdevMultiplier: ST_DEV_MULTI,
      }),
      format: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
    };

    // Wards destroyed
    const playerWardsDestroyed = localPlayer.wardsKilled;
    vision.stats[2] = {
      key: "wardsDestroyed",
      weight: 1,
      title: [
        "lol:postmatch.advancedStatsSubHeaders.wardsDestroyed",
        "Wards Destroyed",
      ],
      score: statPerformance({
        playerVal: playerWardsDestroyed,
        benchmarkMean: eogBenchmark.ward_kills?.mean,
        benchmarkStdev: eogBenchmark.ward_kills?.stdev,
        lowerBoundStdevMultiplier: ST_DEV_MULTI,
        upperBoundStdevMultiplier: ST_DEV_MULTI,
      }),
      format: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
    };
  }
}

function aramStats({
  player,
  match,
  benchmarkData = [],
  playerPerformance = {},
}: {
  player: MatchParticipant;
  match: Match;
  benchmarkData: any /* eslint-disable-line @typescript-eslint/no-explicit-any */;
  playerPerformance: any /* eslint-disable-line @typescript-eslint/no-explicit-any */;
}) {
  const matchMins = Math.round(match.gameDuration / 60) || 1; // end of game
  const benchmarkMins = matchMins;

  const eogBenchmark = benchmarkData[benchmarkMins]
    ? benchmarkData[benchmarkMins]
    : benchmarkData.at(-1);

  if (!eogBenchmark) return;

  const championPrimaryClass = getChampionPrimaryClass(player.championId);
  const championSubClass = getChampionSubClass(player.championId);
  // const playerTeamId = player.teamId;

  const frontlineClasses = [
    SECONDARY_CLASSES.JUGGERNAUT,
    SECONDARY_CLASSES.VANGUARD,
    SECONDARY_CLASSES.WARDEN,
  ];

  // 1) Damage
  const damage = playerPerformance.categories[0];
  // Damage
  damage.stats[0] = {
    weight: 1,
    title: ["common:stats.damageDealt", "Damage Dealt"],
    score: statPerformance({
      playerVal: player.totalDamageDealtToChampions,
      benchmarkMean: eogBenchmark.total_damage_done_to_champions?.mean,
      benchmarkStdev: eogBenchmark.total_damage_done_to_champions?.stdev,
      lowerBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
      upperBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
    }),
    format: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
  };

  // Damage per gold
  damage.stats[1] = {
    weight: 1,
    title: ["common:stats.damagePerGoldSpent", "Damage / Gold Spent"],
    score: statPerformance({
      playerVal: player.totalDamageDealtToChampions / player.goldSpent,
      benchmarkMean:
        eogBenchmark.total_damage_done_to_champions?.mean /
        eogBenchmark.gold_spent?.mean,
      benchmarkStdev:
        eogBenchmark.total_damage_done_to_champions?.stdev /
        eogBenchmark.gold_spent?.stdev,
      lowerBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
      upperBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
    }),
    format: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  };

  if (
    championPrimaryClass === PRIMARY_CLASSES.tank ||
    frontlineClasses.includes(championSubClass)
  ) {
    // Damage taken
    damage.stats[2] = {
      weight: 1,
      title: ["common:stats.damageTaken", "Damage Taken"],
      score: statPerformance({
        playerVal: player.totalDamageTaken,
        benchmarkMean: eogBenchmark.total_damage_taken?.mean,
        benchmarkStdev: eogBenchmark.total_damage_taken?.stdev,
        lowerBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
        upperBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
      }),
      format: {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
    };
  } else {
    damage.stats[2] = {
      weight: 1,
      title: [
        "common:stats.damageDealtPerDamageTaken",
        "Damage Dealt / Damage Taken",
      ],
      score: statPerformance({
        playerVal: player.totalDamageDealtToChampions / player.totalDamageTaken,
        benchmarkMean:
          eogBenchmark.total_damage_done_to_champions?.mean /
          eogBenchmark.total_damage_taken?.mean,
        benchmarkStdev:
          eogBenchmark.total_damage_done_to_champions?.stdev /
          eogBenchmark.total_damage_taken?.stdev,
        lowerBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
        upperBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
      }),
      format: {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      },
    };
  }

  // 2) Combat
  const combat = playerPerformance.categories[1];
  combat.stats[0] = {
    weight: 1,
    title: ["lol:coaching.killParticipation", "Kill Participation"],
    score: statPerformance({
      playerVal: player.kills + player.assists,
      benchmarkMean: eogBenchmark.kills?.mean + eogBenchmark.assists?.mean,
      benchmarkStdev: eogBenchmark.kills?.stdev + eogBenchmark.assists?.stdev,
      lowerBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
      upperBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
    }),
    format: {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
  };

  combat.stats[1] = {
    weight: 1,
    title: ["common:stats.kda", "KDA"],
    score: statPerformance({
      playerVal: (player.kills + player.assists) / player.deaths,
      benchmarkMean:
        (eogBenchmark.kills?.mean + eogBenchmark.assists?.mean) /
        eogBenchmark.deaths?.mean,
      benchmarkStdev:
        (eogBenchmark.kills?.stdev + eogBenchmark.assists?.stdev) /
        eogBenchmark.deaths?.stdev,
      lowerBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
      upperBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
    }),
    format: {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
  };

  // Deaths
  const playerDeaths = player.deaths;
  combat.stats[2] = {
    weight: 1,
    title: ["common:stats.deaths", "Deaths"],
    score: statPerformance({
      playerVal: playerDeaths,
      benchmarkMean: eogBenchmark.deaths?.mean,
      benchmarkStdev: eogBenchmark.deaths?.stdev,
      lowerBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
      upperBoundStdevMultiplier: ST_DEV_MULTI_ARAM,
      lowerIsBetter: true,
    }),
    format: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
  };
}

export function coachingPerformance({
  localPlayer,
  playerRankDetails,
  match,
  timeline,
  benchmarkData,
  queue,
  lateGameStartTimestamp,
  playerRank,
}: {
  localPlayer: MatchParticipant;
  playerRankDetails: any /* eslint-disable-line @typescript-eslint/no-explicit-any */;
  match: Match;
  timeline: MatchTimeline;
  benchmarkData: ChampionByMinuteStats;
  queue: any /* eslint-disable-line @typescript-eslint/no-explicit-any */;
  lateGameStartTimestamp: number;
  playerRank: symbol;
}): PlayerPerformance {
  // Cards: Combat, Farming, Vision
  const playerPerformance: PlayerPerformance = {
    loading: true,
    tooShort: false,
    categories:
      QUEUE_CATEGORIES[queue] || QUEUE_CATEGORIES[QUEUE_SYMBOLS.rankedSoloDuo],
    runesStats: false,
    playerDeaths: [],
    playerFarm: [],
  };

  switch (queue) {
    case QUEUE_SYMBOLS.aram:
    case QUEUE_SYMBOLS.clashAram:
      playerPerformance.runesStats = true;
      break;
  }

  if (!localPlayer || !match || !timeline || !benchmarkData)
    return playerPerformance;

  // Mutate playerPerformance based on the played queue
  switch (queue) {
    case QUEUE_SYMBOLS.aram: {
      aramStats({
        player: localPlayer,
        match,
        benchmarkData,
        playerPerformance,
      });
      break;
    }
    default:
    case undefined:
    case QUEUE_SYMBOLS.rankedFlex:
    case QUEUE_SYMBOLS.rankedSoloDuo: {
      summonersRiftStats({
        localPlayer,
        match,
        timeline,
        benchmarkData,
        playerPerformance,
        lateGameStartTimestamp,
        playerRank,
      });
      const { playerDeaths, playerFarm } = analyzeTimelinePerformance({
        localPlayer,
        match,
        timeline,
        lateGameStartTimestamp,
      });
      performanceFeedback({
        localPlayer,
        playerRankDetails,
        playerPerformance,
        match,
        playerDeaths,
        playerFarm,
        benchmarkData,
        lateGameStartTimestamp,
      });
      break;
    }
  }

  for (const index in playerPerformance.categories) {
    // Compute scores based on sub stats
    const category = playerPerformance.categories[index];
    category.performance = categoryPerf(category);
  }

  playerPerformance.tooShort = matchTooShort(queue, match);
  playerPerformance.loading = false;

  return playerPerformance;
}

/**
 * Score of a particular category derived from its child stats
 */
function categoryPerf(category: PlayerPerformance["categories"][number]) {
  const minAvg =
    category.stats.reduce((acc, curr) => {
      return acc + (curr.score.min ?? 0);
    }, 0) / (category.stats.length || 1);

  const maxAvg =
    category.stats.reduce((acc, curr) => {
      return acc + (curr.score.max ?? 0);
    }, 0) / (category.stats.length || 1);

  const scoreAvg =
    category.stats.reduce((acc, curr) => {
      return acc + (curr.score.score ?? 0);
    }, 0) / (category.stats.length || 1);

  const valAvg =
    category.stats.reduce((acc, curr) => {
      return acc + (curr.score.val ?? 0);
    }, 0) / (category.stats.length || 1);

  return {
    max: maxAvg,
    min: minAvg,
    score: scoreAvg,
    val: valAvg,
  };
}
